import { Check, CircleCheck, Info } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { cn } from "@/lib/utils";

import { SupportButton } from "components/snackbars/SupportButton";
import { ProductFeature, ProductIcon } from "data/models";
import { toTitleCase } from "utils/commons";

const formatPrice = (amount: number) =>
  amount.toLocaleString(undefined, { minimumFractionDigits: 0 });

type ProductCardProps = {
  priceId: string | null;
  title: string;
  dollarCost: number | null;
  cadence: "month" | "year";
  selected: boolean;
  disabled: boolean;
  type: "selectable" | "contact" | "current" | "trial";
  features: ProductFeature[];
  handleSelect: (selected: string) => void;
};

const getIcon = (iconType: ProductIcon) => {
  if (iconType === "info") return <Info className="size-4 text-info" />;
  return <CircleCheck className="size-4 text-success" />;
};

export const ProductCard = (props: ProductCardProps) => {
  const cadenceShort = props.cadence === "year" ? "yr" : "mo";

  return (
    <Card
      className={cn(
        "flex flex-col rounded-xl transition-shadow",
        props.type === "current" && "bg-muted/40",
        props.selected &&
          "ring-2 ring-primary shadow-md border-primary",
      )}
    >
      <CardHeader className="flex-row items-start justify-between gap-2 space-y-0">
        <CardTitle className="text-lg">
          {toTitleCase(props.title)}
        </CardTitle>
        {props.type === "current" && (
          <Badge variant="secondary">Current plan</Badge>
        )}
        {props.type === "trial" && (
          <Badge variant="outline">Trial available</Badge>
        )}
        {props.selected && props.type !== "current" && (
          <Badge className="gap-1">
            <Check className="size-3" />
            Selected
          </Badge>
        )}
      </CardHeader>
      <CardContent className="flex flex-1 flex-col gap-4">
        <div className="flex items-baseline gap-1">
          {props.type === "contact" ? (
            <p className="text-2xl font-semibold">Contact us</p>
          ) : props.dollarCost === null ? (
            <p className="text-4xl font-semibold">-</p>
          ) : (
            <>
              <p className="text-4xl font-semibold tracking-tight">
                ${formatPrice(props.dollarCost)}
              </p>
              <span className="text-muted-foreground text-sm">
                /{cadenceShort}
              </span>
            </>
          )}
        </div>
        <div className="flex flex-col gap-1.5">
          {props.features
            .filter((x) => !x.cadence || x.cadence === props.cadence)
            .map((f, i) => (
              <div
                key={i}
                className="flex items-center justify-between gap-2"
              >
                <div className="flex items-center gap-2">
                  {getIcon(f.icon)}
                  <p className="text-sm">{f.text}</p>
                </div>
                {f.chip && <Badge variant="outline">{f.chip.text}</Badge>}
              </div>
            ))}
        </div>
      </CardContent>
      <CardFooter className="mt-auto">
        {props.type === "contact" ? (
          <SupportButton text="Contact" variant="contained" />
        ) : props.selected ? (
          <Button className="w-full" disabled>
            Selected
          </Button>
        ) : (
          <Button
            className="w-full"
            variant={props.type === "trial" ? "outline" : "default"}
            disabled={props.disabled}
            onClick={() => props.handleSelect(props.priceId ?? "")}
          >
            {props.type === "selectable" && "Select"}
            {props.type === "trial" && "Start Trial"}
            {props.type === "current" && "Current"}
          </Button>
        )}
      </CardFooter>
    </Card>
  );
};
